import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Turnstile from "react-turnstile";
import AuthLayout from "./AuthLayout";
import { Button, Icon, Input } from "../../components/ui";
import { authService, passwordIssues, validEmail } from "../../services/auth";
import { CAPTCHA } from "../../services/config";
import { useToast } from "../../context/ToastContext";
import { useTheme } from "../../context/ThemeContext";

const strength = (p) => {
  let s = 0;
  if (p.length >= 8) s++;
  if (p.length >= 12) s++;
  if (/[A-Z]/.test(p) && /[a-z]/.test(p)) s++;
  if (/\d/.test(p)) s++;
  if (/[^A-Za-z0-9]/.test(p)) s++;
  return Math.min(4, s);
};
const LV = ["อ่อนมาก", "อ่อน", "พอใช้", "ดี", "แข็งแรง"];

export default function Register() {
  const navigate = useNavigate();
  const toast = useToast();
  const { theme } = useTheme();
  const [form, setForm] = useState({ email: "", password: "", confirm: "" });
  const [agree, setAgree] = useState(false);
  const [captcha, setCaptcha] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const st = strength(form.password);
  const issues = passwordIssues(form.password);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!validEmail(form.email)) return setError("รูปแบบอีเมลไม่ถูกต้อง");
    if (issues.length) return setError("รหัสผ่านต้อง" + issues.join(" "));
    if (form.password !== form.confirm) return setError("รหัสผ่านทั้งสองช่องไม่ตรงกัน");
    if (!agree) return setError("กรุณายอมรับข้อกำหนดการใช้งาน");
    if (CAPTCHA.siteKey && !captcha && !CAPTCHA.masterToken) return setError("กรุณายืนยันว่าคุณไม่ใช่บอท");
    setLoading(true);
    try {
      await authService.register({ email: form.email, password: form.password, captchaToken: captcha });
      toast("สมัครสมาชิกสำเร็จ รับ token ฟรีแล้ว!", "success");
      navigate("/chat", { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title="สร้างบัญชี MixAI" subtitle="ใช้แค่อีเมลและรหัสผ่าน รับ 40,000 token ฟรีทันที" footer={<>มีบัญชีอยู่แล้ว? <Link to="/login">เข้าสู่ระบบ</Link></>}>
      <form onSubmit={submit} noValidate className="auth-form">
        <Input label="อีเมล" type="email" icon="mail" placeholder="you@example.com" value={form.email} onChange={set("email")} autoComplete="email" autoFocus />
        <Input label="รหัสผ่าน" type="password" icon="lock" placeholder="อย่างน้อย 8 ตัว มีตัวอักษรและตัวเลข" value={form.password} onChange={set("password")} autoComplete="new-password" />
        {form.password ? (
          <div className="pw-meter">
            <div className="pw-bars">{[0, 1, 2, 3].map((i) => <span key={i} className={i < st ? `on lv${st}` : ""} />)}</div>
            <span className="tiny">{LV[st]}</span>
          </div>
        ) : null}
        <Input
          label="ยืนยันรหัสผ่าน"
          type="password"
          icon="lock"
          placeholder="พิมพ์รหัสผ่านอีกครั้ง"
          value={form.confirm}
          onChange={set("confirm")}
          autoComplete="new-password"
          error={form.confirm && form.confirm !== form.password ? "รหัสผ่านไม่ตรงกัน" : ""}
        />
        <label className="check">
          <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
          <span>ฉันยอมรับ <a href="#terms" onClick={(e) => e.preventDefault()}>ข้อกำหนดการใช้งาน</a> และ <a href="#privacy" onClick={(e) => e.preventDefault()}>นโยบายความเป็นส่วนตัว</a></span>
        </label>
        {CAPTCHA.siteKey ? (
          <div className="mb-16">
            <Turnstile sitekey={CAPTCHA.siteKey} theme={theme} onVerify={setCaptcha} onExpire={() => setCaptcha("")} />
          </div>
        ) : null}
        {error ? <div className="form-error" role="alert">{error}</div> : null}
        <Button type="submit" variant="primary" block size="lg" loading={loading}>
          {loading ? "กำลังสร้างบัญชี..." : "สมัครฟรี"}
        </Button>
        <ul className="auth-perks">
          <li><Icon name="check" size={14} stroke={2.4} /> ฟรี 30,000 token ทุกเดือน + โบนัส 10,000</li>
          <li><Icon name="check" size={14} stroke={2.4} /> ไม่ต้องใช้บัตรเครดิต</li>
        </ul>
      </form>
    </AuthLayout>
  );
}
